import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useClient } from "urql";
import Cropper from "react-easy-crop";
import "react-easy-crop/react-easy-crop.css";
import CustomModal from "../configuracion/components/customModal";
import FeatherIcon from "feather-icons-react";
import "../repositorio/UploadModal.styles.scss";
import "./ProfileImageUploadModal.scss";
import { importAlertState } from "../../redux/actions/alert";
import { uploadToS3 } from "../../utils/s3Upload";
import { getCroppedImageBlob } from "../../utils/cropImage";
import {
  requestProfileImageUploadUrl,
  SetProfileImage,
} from "../../graphql/users";

const ALLOWED_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
]);

const MAX_FILE_SIZE_MB = 5;

const ProfileImageUploadModal = ({ open, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const client = useClient();
  const { executeMutation: setProfileImage } = SetProfileImage();

  const [step, setStep] = useState("select");
  const [imageSrc, setImageSrc] = useState(null);
  const [selectedFileMeta, setSelectedFileMeta] = useState(null);
  const [sourceFile, setSourceFile] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const fileInputRef = useRef(null);
  const imageSrcRef = useRef(null);

  const revokeImageSrc = useCallback(() => {
    if (imageSrcRef.current) {
      URL.revokeObjectURL(imageSrcRef.current);
      imageSrcRef.current = null;
    }
    setImageSrc(null);
  }, []);

  const resetState = useCallback(() => {
    revokeImageSrc();
    setStep("select");
    setSelectedFileMeta(null);
    setSourceFile(null);
    setIsDragOver(false);
    setIsUploading(false);
    setUploadProgress(0);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [revokeImageSrc]);

  useEffect(() => {
    if (open) {
      resetState();
    }
    return () => {
      if (imageSrcRef.current) {
        URL.revokeObjectURL(imageSrcRef.current);
        imageSrcRef.current = null;
      }
    };
  }, [open, resetState]);

  const showError = useCallback(
    (content) => {
      dispatch(
        importAlertState({
          content,
          state: "error",
          duration: 5,
        }),
      );
    },
    [dispatch],
  );

  const validateFile = (file) => {
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > MAX_FILE_SIZE_MB) {
      showError(
        `El archivo excede el tamaño máximo de ${MAX_FILE_SIZE_MB} MB (${fileSizeMB.toFixed(2)} MB).`,
      );
      return false;
    }
    const mime = (file.type || "").toLowerCase();
    if (!ALLOWED_TYPES.has(mime)) {
      showError("Solo se permiten imágenes JPG, PNG, GIF o WebP.");
      return false;
    }
    return true;
  };

  const handleFileSelect = (file) => {
    if (!validateFile(file)) return;

    revokeImageSrc();
    const url = URL.createObjectURL(file);
    imageSrcRef.current = url;
    setImageSrc(url);
    const isGif = file.type === "image/gif";
    setSourceFile(file);
    setSelectedFileMeta({
      name: file.name,
      size: file.size,
      type: file.type,
      isGif,
    });
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setStep(isGif ? "gifPreview" : "crop");
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) handleFileSelect(files[0]);
  };

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) handleFileSelect(files[0]);
  };

  const handleBackToSelect = () => {
    revokeImageSrc();
    setSelectedFileMeta(null);
    setSourceFile(null);
    setStep("select");
    setCroppedAreaPixels(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const uploadProfileFile = async (uploadFile) => {
    const { uploadUrl, key } = await requestProfileImageUploadUrl(client, {
      fileName: uploadFile.name,
      fileType: uploadFile.type || "application/octet-stream",
    });

    await uploadToS3(uploadFile, uploadUrl, (progress) => {
      setUploadProgress(progress);
    });

    const result = await setProfileImage({ key });
    if (result.error) {
      throw new Error(
        result.error.graphQLErrors?.[0]?.message ||
          result.error.message ||
          "No se pudo guardar la foto de perfil",
      );
    }

    dispatch(
      importAlertState({
        content: "Foto de perfil actualizada correctamente.",
        state: "success",
        duration: 4,
      }),
    );

    await new Promise((resolve) => setTimeout(resolve, 300));
    if (onSuccess) await onSuccess();
    onClose();
  };

  const onCropComplete = useCallback((_croppedArea, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleConfirm = async () => {
    if (step !== "crop" && step !== "gifPreview") return;

    if (step === "crop" && (!imageSrc || !croppedAreaPixels)) {
      showError("Ajusta el recorte antes de guardar.");
      return;
    }

    if (step === "gifPreview" && !sourceFile) {
      showError("No hay archivo para subir.");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      let uploadFile;
      if (step === "gifPreview") {
        uploadFile = sourceFile;
      } else {
        const croppedBlob = await getCroppedImageBlob(
          imageSrc,
          croppedAreaPixels,
        );
        uploadFile = new File([croppedBlob], "profile.jpg", {
          type: "image/jpeg",
        });
      }

      await uploadProfileFile(uploadFile);
    } catch (error) {
      console.error("Profile image upload error:", error);
      showError(
        error?.message ||
          "Hubo un problema al subir la imagen. Intenta de nuevo.",
      );
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleCancel = () => {
    if (isUploading) return;
    resetState();
    onClose();
  };

  const modalTitle =
    step === "crop"
      ? "Recortar foto de perfil"
      : step === "gifPreview"
        ? "Vista previa GIF"
        : "Foto de perfil";

  const renderSelectStep = () => (
    <div
      className={`repositorio-upload-zone ${isDragOver ? "drag-over" : ""}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => fileInputRef.current?.click()}
      role="presentation"
    >
      <div className="repositorio-upload-icon">
        <FeatherIcon icon="upload-cloud" size={48} />
      </div>
      <div className="repositorio-upload-text">
        Arrastra una imagen aquí o haz clic para seleccionar
      </div>
      <div className="repositorio-upload-subtext">
        JPG, PNG, GIF o WebP (máx. {MAX_FILE_SIZE_MB} MB). Podrás recortarla antes
        de guardar.
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={handleFileInputChange}
        style={{ display: "none" }}
      />
    </div>
  );

  const renderCropStep = () => (
    <>
      <div className="profile-image-crop">
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          aspect={1}
          cropShape="rect"
          showGrid
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={onCropComplete}
        />
      </div>
      <p className="profile-image-crop__hint">
        Arrastra para encuadrar y usa el zoom para ajustar
      </p>
      <div className="profile-image-crop-controls">
        <label className="profile-image-crop-controls__label">
          <span>Zoom</span>
          <input
            type="range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
          />
        </label>
      </div>
      <button
        type="button"
        className="profile-image-crop-back"
        onClick={handleBackToSelect}
      >
        Elegir otra imagen
      </button>
    </>
  );

  const renderGifPreviewStep = () => (
    <>
      <div className="profile-image-gif-preview">
        <img
          src={imageSrc}
          alt="Vista previa del GIF"
          className="profile-image-gif-preview__img"
        />
      </div>
      <p className="profile-image-crop__hint">
        Los GIF animados se suben sin recorte para conservar la animación.
      </p>
      <button
        type="button"
        className="profile-image-crop-back"
        onClick={handleBackToSelect}
      >
        Elegir otra imagen
      </button>
    </>
  );

  const renderUploadingStep = () => (
    <div className="repositorio-upload-progress-container">
      <div className="repositorio-upload-file-item">
        <div className="repositorio-upload-progress">
          <div className="repositorio-upload-progress-bar">
            <div
              className="repositorio-upload-progress-bar-fill"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <div className="repositorio-upload-progress-text">
            Subiendo... {Math.floor(uploadProgress)}%
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <CustomModal
      open={open}
      onClose={handleCancel}
      title={modalTitle}
      classNameModal="repositorio-upload-modal profile-image-upload-modal"
      classNameContent="repositorio-upload-modal-content-wrapper"
      content={
        <div className="repositorio-upload-modal-content">
          <div className="repositorio-upload-content-top">
            {step === "select" && !isUploading && renderSelectStep()}
            {step === "crop" && !isUploading && renderCropStep()}
            {step === "gifPreview" && !isUploading && renderGifPreviewStep()}
            {isUploading && renderUploadingStep()}
          </div>
        </div>
      }
      confirmLabel={
        step === "crop" || step === "gifPreview" ? (
          <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            Guardar
            <FeatherIcon icon="upload" size={14} />
          </span>
        ) : null
      }
      confirmDisabled={
        isUploading ||
        (step === "crop" && !croppedAreaPixels) ||
        (step === "gifPreview" && !sourceFile)
      }
      isLoading={isUploading}
      cancelarLabel="Cancelar"
      onConfirm={handleConfirm}
      size="tiny"
      classNameTitle="repositorio-upload-modal-title"
      dimmer={{ style: { zIndex: 10000, backgroundColor: "rgba(0,0,0,0.5)" } }}
      modalStyle={{
        maxWidth: step === "crop" ? "560px" : "520px",
        minWidth: "min(90vw, 480px)",
        margin: "0 auto",
      }}
    />
  );
};

export default ProfileImageUploadModal;
